import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { operations } from "@/api/schema";

import { toOrder, type RawOrderListItem } from "../lib/order-list-vm";

export interface OrderFilters {
  marketplace?: string;
  /** `| undefined` (not just optional) so parseOrderSearch can explicitly null
   * it out — see order-url-state.ts's parseOrderSearch for why an omitted key
   * isn't enough under TanStack Router's raw-passthrough merge. */
  status?: string[] | undefined;
  payment?: string;
  seller_id?: string;
  marketplace_status?: string;
  exception_only?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
  date_from?: string;
  date_to?: string;
  /** Filters on review_reasons: "any" for at least one of the post-ingest exception reasons, or a specific reason to filter on that reason alone. */
  attention?: AttentionReason | "any";
  /** present = a Sino AWB is cached for the order; missing = none yet. */
  awb?: "present" | "missing";
  /** Seller Own Fleet flag, as a string so it round-trips through the URL cleanly. */
  sof?: "true" | "false";
  /** true = at least one line item is a GWP freebie, as a string for the same reason as `sof`. */
  gift?: "true" | "false";
  /** Ship-by deadline bucket relative to now (Asia/Bangkok calendar day for today). */
  ship_by?: "overdue" | "today" | "next_24h";
  /** Exact orders.courier value. */
  courier?: string;
  /** Comma-joined exact orders.payment_channel values (e.g. TrueMoney,SPayLater). */
  payment_channel?: string;
}

type ListOrdersQuery = NonNullable<operations["listOrders"]["parameters"]["query"]>;

/** The post-ingest exception codes AttentionCounts.by_reason and the `attention` filter group by (BE schema). */
export type AttentionReason = Exclude<NonNullable<ListOrdersQuery["attention"]>, "any">;

/**
 * Builds the BE query object shared by GET /v1/orders, /v1/orders/kpis and
 * /v1/orders/attention-counts — only include defined values to avoid sending
 * empty strings. exactOptionalPropertyTypes=true prevents assigning
 * `undefined` via normal assignment; we spread conditionally and cast the
 * result to the schema type at this boundary.
 */
export function toListQuery(f: OrderFilters): ListOrdersQuery {
  return {
    ...(f.marketplace !== undefined && { marketplace: f.marketplace }),
    ...(f.status !== undefined && f.status.length > 0 && { status: f.status.join(",") }),
    ...(f.awb !== undefined && { awb: f.awb }),
    ...(f.sof !== undefined && { sof: f.sof === "true" }),
    ...(f.gift !== undefined && { gift: f.gift === "true" }),
    ...(f.ship_by !== undefined && { ship_by: f.ship_by }),
    ...(f.courier !== undefined && { courier: f.courier }),
    ...(f.payment_channel !== undefined && { payment_channel: f.payment_channel }),
    ...(f.payment !== undefined && { payment: f.payment }),
    ...(f.seller_id !== undefined && { seller_id: f.seller_id }),
    ...(f.marketplace_status !== undefined && { marketplace_status: f.marketplace_status }),
    ...(f.exception_only !== undefined && { exception_only: f.exception_only }),
    ...(f.search !== undefined && { search: f.search }),
    ...(f.limit !== undefined && { limit: f.limit }),
    ...(f.offset !== undefined && { offset: f.offset }),
    ...(f.date_from !== undefined && { date_from: f.date_from }),
    ...(f.date_to !== undefined && { date_to: f.date_to }),
    ...(f.attention !== undefined && { attention: f.attention }),
  } as ListOrdersQuery;
}

export function useOrders(f: OrderFilters) {
  return useQuery({
    queryKey: ["orders", f],
    queryFn: async () => {
      const query = toListQuery(f);

      const { data, error } = await apiClient.GET("/v1/orders", {
        params: { query },
      });
      if (error) throw error;
      // BE returns a thinner projection than the table renders; MSW serves the
      // full view-model. toOrder() bridges both and defaults money/items so the
      // Orders page never crashes on a thin response (see order-list-vm).
      return {
        orders: (data?.data ?? []).map((o) => toOrder(o as unknown as RawOrderListItem)),
        total: data?.total ?? 0,
      };
    },
  });
}
